import type { ItemInstance } from '@headless-tree/core';

/**
 * Keep the items whose name matches `search`, plus their ancestors (to preserve the
 * tree structure) and their descendants (so a cluster-name hit still shows its
 * instances instead of an empty cluster).
 *
 * Relies on the flat item list being depth-first ordered, so the descendants of an
 * item are the contiguous run of deeper-level items that follows it.
 */
export function filterTreeItemsBySearch<T>(
  items: ItemInstance<T>[],
  search: string
): ItemInstance<T>[] {
  const query = search.trim().toLowerCase();
  if (!query) {
    return items;
  }

  const levels = items.map((item) => item.getItemMeta().level);
  const keep = new Set<number>();

  items.forEach((item, index) => {
    if (!item.getItemName().toLowerCase().includes(query)) {
      return;
    }
    keep.add(index);

    // Ancestors: walk backwards, tracking each shallower level we cross.
    let level = levels[index];
    for (let i = index - 1; i >= 0 && level > 0; i--) {
      if (levels[i] < level) {
        keep.add(i);
        level = levels[i];
      }
    }

    // Descendants: the contiguous deeper-level run that follows.
    for (let i = index + 1; i < items.length && levels[i] > levels[index]; i++) {
      keep.add(i);
    }
  });

  return items.filter((_, index) => keep.has(index));
}
